/**
 * ElevenLabsVoice Component
 * Uses official @elevenlabs/react SDK for Conversational AI
 * Admin/Owner only feature
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useConversation } from '@elevenlabs/react';
import { FaMicrophone, FaStop, FaSpinner } from 'react-icons/fa';
import { ELEVENLABS_S2S_ENABLED, ELEVENLABS_CONFIG } from '../config/chatConfig';
import { useVoice } from '../context/VoiceContext';
import memoryApi from '../api/memoryApi';
import { authedFetch } from '../api/authedFetch';

const DEBUG_VOICE = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_VOICE === 'true';

const ElevenLabsVoice = React.forwardRef(({
  user,
  onTranscriptUpdate,
  onVoiceSessionChange,
  currentSessionId,
  navigate,
  refreshSessions,
  size = 12
}, ref) => {
  const [error, setError] = useState(null);
  const volumeIntervalRef = useRef(null);
  const transcriptRef = useRef([]); // Store transcript for saving on disconnect
  const sessionIdRef = useRef(currentSessionId); // Track session for saving

  // Voice overlay context (animated balls like OpenAI)
  const { showVoiceOverlay, hideVoiceOverlay, setMicAmplitude } = useVoice();

  // Check if user is admin/owner
  const isAdminOrOwner = ['owner', 'admin'].includes(user?.['custom:membership']);
  const hasAccess = ELEVENLABS_S2S_ENABLED && isAdminOrOwner;

  // Get signed URL from backend (protects API key)
  const getSignedUrl = async () => {
    const response = await authedFetch(ELEVENLABS_CONFIG.signedUrlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'elevenlabs_signed_url',
        agent_id: ELEVENLABS_CONFIG.agentId,
        user_id: user?.sub || 'anonymous'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to get signed URL');
    }

    const data = await response.json();
    return data.signed_url;
  };

  // Start polling volume levels for wave animation
  const startVolumePolling = useCallback((conv) => {
    if (volumeIntervalRef.current) {
      clearInterval(volumeIntervalRef.current);
    }

    volumeIntervalRef.current = setInterval(() => {
      // Get input (mic) or output (AI) volume depending on who's talking
      const inputVol = conv.getInputVolume?.() || 0;
      const outputVol = conv.getOutputVolume?.() || 0;

      // Use whichever is higher, normalize to 0.1-3 range for wave
      const volume = Math.max(inputVol, outputVol);
      const normalizedAmplitude = Math.max(0.1, volume * 3);

      setMicAmplitude(normalizedAmplitude);
    }, 50); // Poll every 50ms for smooth animation
  }, [setMicAmplitude]);

  const stopVolumePolling = useCallback(() => {
    if (volumeIntervalRef.current) {
      clearInterval(volumeIntervalRef.current);
      volumeIntervalRef.current = null;
    }
    setMicAmplitude(0);
  }, [setMicAmplitude]);

  // Use the official ElevenLabs conversation hook
  const conversation = useConversation({
    onConnect: () => {
      if (DEBUG_VOICE) console.log('11Labs: Connected');
      // Show processing overlay (waiting for user to speak)
      showVoiceOverlay('processing', true, () => {
        conversation.endSession();
      });
      // Start volume polling for wave animation
      startVolumePolling(conversation);
      if (onVoiceSessionChange) {
        onVoiceSessionChange(true);
      }
    },
    onDisconnect: () => {
      // TEMP: Always log to debug save issue
      console.log('11Labs: onDisconnect - transcript:', transcriptRef.current.length, 'session:', sessionIdRef.current);
      stopVolumePolling();
      hideVoiceOverlay();

      // Save transcript using fetch with keepalive for reliability
      // This ensures save happens even if user navigates away
      if (transcriptRef.current.length > 0 && sessionIdRef.current) {
        try {
          if (DEBUG_VOICE) console.log('11Labs: Saving transcript to session:', sessionIdRef.current);

          // Filter and deduplicate
          const cleanTranscript = transcriptRef.current.filter((msg, index, arr) => {
            if (!msg || !msg.role || !msg.text) return false;
            return !arr.slice(0, index).some(prev =>
              prev && prev.role === msg.role &&
              prev.text === msg.text &&
              prev.timestamp === msg.timestamp
            );
          });

          if (cleanTranscript.length > 0) {
            // Convert to chat message format - use 'text' field for frontend compatibility
            const chatMessages = cleanTranscript.map(msg => ({
              role: msg.role === 'ai' ? 'assistant' : msg.role,
              text: msg.text,
              timestamp: msg.timestamp,
              type: 'voice'
            }));

            // Atomic append — server-side list_append. Conflict-free against
            // concurrent text saves on the same session (the prior get-then-put
            // pattern lost messages when both flows raced).
            console.log('11Labs: Appending', chatMessages.length, 'voice messages atomically');
            memoryApi.appendMessages(chatMessages, sessionIdRef.current)
              .then(result => {
                console.log('11Labs: Append SUCCESS, new message_count:', result.message_count);
              })
              .catch(saveError => {
                console.error('11Labs: Append FAILED:', saveError);
              });
          }
        } catch (saveError) {
          console.error('11Labs: Failed to save transcript:', saveError);
        }

        // Clear transcript
        transcriptRef.current = [];
      }

      if (onVoiceSessionChange) {
        onVoiceSessionChange(false);
      }
    },
    onMessage: (message) => {
      if (DEBUG_VOICE) console.log('11Labs: Message received:', message);
      // Handle transcript updates and store for saving
      // SDK message format: { source: 'user'|'ai', message: string }
      const messageText = message.message || message.text || message.content;
      const messageSource = message.source || message.role;

      if (!messageText) {
        if (DEBUG_VOICE) console.warn('11Labs: Empty message text, skipping');
        return;
      }

      if (messageSource === 'user') {
        const msg = {
          role: 'user',
          text: messageText,
          timestamp: Date.now()
        };
        transcriptRef.current.push(msg);
        if (DEBUG_VOICE) console.log('11Labs: Added user message, transcript length:', transcriptRef.current.length);
        if (onTranscriptUpdate) {
          onTranscriptUpdate(msg);
        }
      } else if (messageSource === 'ai' || messageSource === 'assistant') {
        const msg = {
          role: 'ai',
          text: messageText,
          timestamp: Date.now()
        };
        transcriptRef.current.push(msg);
        if (DEBUG_VOICE) console.log('11Labs: Added AI message, transcript length:', transcriptRef.current.length);
        if (onTranscriptUpdate) {
          onTranscriptUpdate(msg);
        }
      } else {
        if (DEBUG_VOICE) console.warn('11Labs: Unknown message source:', messageSource);
      }
    },
    onError: (err) => {
      console.error('11Labs: Error:', err);
      setError(err.message || 'Connection error');
    },
    onModeChange: (mode) => {
      if (DEBUG_VOICE) console.log('11Labs: Mode changed:', mode.mode);
      // mode.mode can be 'speaking', 'listening', 'idle'
      if (mode.mode === 'speaking') {
        showVoiceOverlay('speaking', true, () => {
          conversation.endSession();
        });
      } else if (mode.mode === 'listening') {
        showVoiceOverlay('processing', true, () => {
          conversation.endSession();
        });
      }
    }
  });

  // Start conversation - create session first if needed (like text chat)
  const connect = useCallback(async () => {
    if (conversation.status === 'connected' || conversation.status === 'connecting') {
      return;
    }

    setError(null);
    transcriptRef.current = []; // Clear transcript for new session

    try {
      // Create session first if none exists (same flow as text chat)
      let activeSessionId = currentSessionId;
      if (!activeSessionId) {
        if (DEBUG_VOICE) console.log('11Labs: No session, creating one first...');
        const sessionTitle = `ElevenLabs Voice - ${new Date().toLocaleDateString()}`;
        const newSession = await memoryApi.createSession(sessionTitle);
        activeSessionId = newSession.session_id;
        sessionIdRef.current = activeSessionId;

        // Refresh sidebar and navigate to new session
        if (refreshSessions) refreshSessions();
        if (navigate) {
          navigate(`/chatty/${activeSessionId}`, { replace: true });
        }
        if (DEBUG_VOICE) console.log('11Labs: Created session:', activeSessionId);
      } else {
        sessionIdRef.current = activeSessionId;
      }

      // Request microphone access
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Get signed URL from backend
      const signedUrl = await getSignedUrl();

      // Start the conversation session
      await conversation.startSession({
        signedUrl
      });

      if (DEBUG_VOICE) console.log('11Labs: Voice session started');
    } catch (err) {
      console.error('11Labs: Connection failed:', err);
      setError(err.message || 'Failed to connect');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.status, currentSessionId, navigate, refreshSessions]);

  // Disconnect
  const disconnect = useCallback(async () => {
    await conversation.endSession();
  }, [conversation]);

  // Toggle connection
  const toggleVoiceChat = useCallback(() => {
    if (conversation.status === 'connected') {
      disconnect();
    } else {
      connect();
    }
  }, [conversation.status, connect, disconnect]);

  // Expose toggle to parent
  React.useImperativeHandle(ref, () => ({
    toggleVoiceChat
  }));

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopVolumePolling();
      if (conversation.status === 'connected') {
        conversation.endSession();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopVolumePolling]);

  // Don't render if no access
  if (!hasAccess) {
    return null;
  }

  // Connecting state
  if (conversation.status === 'connecting') {
    return (
      <FaSpinner
        className="animate-spin"
        size={size}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          color: '#8b5cf6',
          pointerEvents: 'none'
        }}
      />
    );
  }

  // Connected state
  if (conversation.status === 'connected') {
    return (
      <FaStop
        size={size}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          color: '#8b5cf6',
          pointerEvents: 'none'
        }}
      />
    );
  }

  // Default state
  return (
    <FaMicrophone
      size={size}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        color: error ? '#ff4444' : '#8b5cf6',
        pointerEvents: 'none'
      }}
    />
  );
});

export default ElevenLabsVoice;
